/**
 * ContactEmailComposer — send an email to a contact with templates, signatures,
 * and the shared AIEmailWriter panel. Pulls the contact MD file under the hood
 * (via /api/ai/write-email) so AI drafts are grounded in the contact's
 * meeting history and analysis.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import {
    Mail, Send, Loader2, Sparkles, FileText, PenLine,
} from 'lucide-react';
import AIEmailWriter from './AIEmailWriter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function applyMerge(str, vars) {
    if (!str) return '';
    return Object.entries(vars).reduce(
        (acc, [k, v]) => acc.split(`{{${k}}}`).join(v || ''),
        str
    );
}

export default function ContactEmailComposer({
    open,
    onOpenChange,
    contact,
    account,
    deal,
    getAuthHeader,
    onSent,
}) {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [templates, setTemplates] = useState([]);
    const [signatures, setSignatures] = useState([]);
    const [templateId, setTemplateId] = useState('none');
    const [signatureId, setSignatureId] = useState('');
    const [showAI, setShowAI] = useState(false);
    const [sending, setSending] = useState(false);

    const mergeVars = useMemo(() => ({
        contact_first_name: contact?.first_name || '',
        contact_last_name: contact?.last_name || '',
        contact_full_name: [contact?.first_name, contact?.last_name].filter(Boolean).join(' '),
        contact_email: contact?.email || '',
        company_name: account?.name || '',
    }), [contact, account]);

    useEffect(() => {
        if (!open) return;
        Promise.all([
            axios.get(`${API}/email-templates`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
            axios.get(`${API}/signatures`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        ]).then(([tRes, sRes]) => {
            setTemplates(Array.isArray(tRes.data) ? tRes.data : (tRes.data?.templates || []));
            const sigs = Array.isArray(sRes.data) ? sRes.data : (sRes.data?.signatures || []);
            setSignatures(sigs);
            const def = sigs.find(s => s.is_default) || sigs[0];
            if (def) setSignatureId(def.signature_id);
        });
    }, [open, getAuthHeader]);

    const applyTemplate = useCallback((id) => {
        setTemplateId(id);
        if (id === 'none') return;
        const t = templates.find(t => t.template_id === id || t.id === id);
        if (!t) return;
        if (t.subject) setSubject(applyMerge(t.subject, mergeVars));
        if (t.body || t.body_html) setBody(applyMerge(t.body || t.body_html, mergeVars));
    }, [templates, mergeVars]);

    const signatureText = useMemo(() => {
        const s = signatures.find(sg => sg.signature_id === signatureId);
        const html = s?.html || s?.signature_html || '';
        return html.replace(/<[^>]+>/g, '').trim();
    }, [signatures, signatureId]);

    const handleAIInsert = useCallback((content) => {
        // AIEmailWriter returns an object with {subject, body} or raw string depending on API.
        if (content && typeof content === 'object') {
            if (content.subject) setSubject(content.subject);
            if (content.body) setBody(content.body);
        } else if (typeof content === 'string') {
            setBody(content);
        }
        setShowAI(false);
    }, []);

    const send = async () => {
        if (!contact?.email) { toast.error('Contact has no email'); return; }
        if (!subject.trim()) { toast.error('Subject required'); return; }
        if (!body.trim()) { toast.error('Body required'); return; }
        setSending(true);
        try {
            const sig = signatures.find(s => s.signature_id === signatureId);
            const sigHtml = sig?.html || sig?.signature_html || '';
            const html = `<div>${body.replace(/\n/g, '<br/>')}</div>${sigHtml ? `<br/>${sigHtml}` : ''}`;
            await axios.post(`${API}/oauth/gmail/send`, {
                to: contact.email,
                subject,
                html,
                contact_id: contact.contact_id,
            }, { headers: getAuthHeader() });
            toast.success('Email sent');
            onSent?.();
            onOpenChange(false);
            // reset
            setSubject(''); setBody(''); setTemplateId('none');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send — is Gmail connected in Settings?');
        } finally {
            setSending(false);
        }
    };

    if (showAI) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-violet-600" /> AI email writer
                        </DialogTitle>
                    </DialogHeader>
                    <AIEmailWriter
                        contactId={contact?.contact_id}
                        dealId={deal?.deal_id}
                        accountId={account?.account_id}
                        existingDraft={body ? `Subject: ${subject}\n\n${body}` : null}
                        onInsert={handleAIInsert}
                        onClose={() => setShowAI(false)}
                    />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contact-email-composer">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="w-4 h-4" /> Email {contact ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() : ''}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-3">
                    <div className="text-xs text-stone-500">
                        To: <span className="font-medium text-stone-700">{contact?.email || '(no email on contact)'}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs flex items-center gap-1">
                                <FileText className="w-3 h-3" /> Template
                            </Label>
                            <Select value={templateId} onValueChange={applyTemplate}>
                                <SelectTrigger data-testid="email-template-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None (custom)</SelectItem>
                                    {templates.map(t => (
                                        <SelectItem key={t.template_id || t.id} value={t.template_id || t.id}>
                                            {t.name || t.title}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs flex items-center gap-1">
                                <PenLine className="w-3 h-3" /> Signature
                            </Label>
                            <Select value={signatureId || 'none'} onValueChange={setSignatureId}>
                                <SelectTrigger data-testid="email-signature-select"><SelectValue placeholder="None" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {signatures.map(s => (
                                        <SelectItem key={s.signature_id} value={s.signature_id}>
                                            {s.name}{s.is_default ? ' (default)' : ''}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label className="text-xs">Subject</Label>
                        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject…" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs">Body</Label>
                            <button
                                type="button"
                                onClick={() => setShowAI(true)}
                                className="flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100"
                                data-testid="ai-writer-button"
                            >
                                <Sparkles className="w-3 h-3" /> AI writer
                            </button>
                        </div>
                        <Textarea
                            value={body}
                            onChange={e => setBody(e.target.value)}
                            rows={14}
                            placeholder="Write your email…"
                            className="text-sm"
                        />
                        {signatureText && (
                            <div className="mt-2 text-[10px] text-stone-400 border-t border-stone-100 pt-2">
                                Signature preview: <span className="italic">{signatureText.slice(0, 100)}{signatureText.length > 100 ? '…' : ''}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-stone-200">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
                    <Button onClick={send} disabled={sending || !contact?.email} data-testid="send-email-button">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                        Send
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
